<template>
  <div class="sql-log-panel">
    <el-card class="workspace-card sql-toolbar-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>车型数据收集</span>
          <div class="header-actions">
            <el-button :loading="sqlLogLoading" @click="loadSqlLogs">刷新</el-button>
            <el-button type="success" plain :loading="sqlLogDownloading" @click="downloadSqlLogs">下载</el-button>
          </div>
        </div>
      </template>

      <div class="sql-toolbar">
        <el-radio-group v-model="sqlLogTable" @change="handleSqlLogTableChange">
          <el-radio-button
            v-for="item in mazdaSqlLogTableOptions"
            :key="item.value"
            :value="item.value"
          >{{ item.value }}</el-radio-button>
        </el-radio-group>

        <el-input
          v-model="sqlLogKeyword"
          clearable
          class="sql-search-input"
          placeholder="搜索 VIN / 文件名 / 设备 / IP / 版本内容"
          @keyup.enter="handleSqlLogSearch"
          @clear="handleSqlLogSearch"
        />
        <el-button type="primary" :loading="sqlLogLoading" @click="handleSqlLogSearch">查询</el-button>
      </div>

      <div class="sql-summary-strip">
        <div class="sql-summary-item">
          <span>当前表</span>
          <strong>{{ sqlLogTable }}</strong>
        </div>
        <div class="sql-summary-item sql-summary-wide">
          <span>数据用途</span>
          <strong>{{ sqlLogTableDesc }}</strong>
        </div>
      </div>
    </el-card>

    <el-card class="workspace-card sql-list-card" shadow="hover">
      <template #header>
        <div class="card-header">
          <span>车型数据列表</span>
          <div class="sql-total-text">{{ sqlLogTotalText }}</div>
        </div>
      </template>

      <div class="table-scroll-shell">
        <div class="table-scroll-inner sql-table-scroll-inner">
          <el-table
            :data="sqlLogItems"
            stripe
            border
            class="result-table"
            v-loading="sqlLogLoading"
            empty-text="暂无车型数据"
            row-key="rowNo"
          >
            <el-table-column
              v-for="column in sqlLogColumns"
              :key="column"
              :prop="column"
              :label="getSqlLogColumnLabel(column)"
              :min-width="getSqlLogColumnMinWidth(column)"
              show-overflow-tooltip
            />
            <el-table-column label="操作" width="120" fixed="right" align="center" class-name="action-column">
              <template #default="scope">
                <el-button type="primary" size="small" @click="showSqlLogDetail(scope.row)">详情</el-button>
              </template>
            </el-table-column>
          </el-table>

          <div class="pagination-wrap sql-pagination-wrap">
            <div class="sql-pagination-left">{{ sqlLogPageStatusText }}</div>
            <div class="sql-pagination-right">
              <span class="pager-label">每页</span>
              <el-select v-model="sqlLogPageSize" class="page-size-select" @change="handleSqlLogSizeChange">
                <el-option :value="20" label="20 条" />
                <el-option :value="50" label="50 条" />
                <el-option :value="100" label="100 条" />
              </el-select>
              <template v-if="sqlLogExactTotal">
                <el-pagination
                  background
                  layout="prev, pager, next"
                  :total="sqlLogTotal"
                  :current-page="sqlLogPage"
                  :page-size="sqlLogPageSize"
                  @current-change="handleSqlLogPageChange"
                />
                <span class="pager-label">跳至</span>
                <el-input v-model="sqlLogJumpPage" class="jump-input" @keyup.enter="handleSqlLogJump" />
                <span class="pager-label">页</span>
                <el-button @click="handleSqlLogJump">确定</el-button>
              </template>
              <template v-else>
                <div class="quick-pager">
                  <el-button
                    :disabled="sqlLogLoading || sqlLogPage <= 1"
                    @click="handleSqlLogPageChange(sqlLogPage - 1)"
                  >
                    上一页
                  </el-button>
                  <el-button
                    type="primary"
                    plain
                    :disabled="sqlLogLoading || !sqlLogHasMore"
                    @click="handleSqlLogPageChange(sqlLogPage + 1)"
                  >
                    下一页
                  </el-button>
                </div>
              </template>
            </div>
          </div>
        </div>
      </div>
    </el-card>

    <el-dialog v-model="sqlLogDetailVisible" title="车型数据详情" width="82%">
      <el-input
        :model-value="sqlLogDetailText"
        type="textarea"
        :rows="20"
        resize="none"
        readonly
        class="sql-detail-textarea"
      />
      <template #footer>
        <div class="dialog-footer">
          <el-button @click="sqlLogDetailVisible = false">关闭</el-button>
        </div>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { toRef } from 'vue';
import { mazdaSqlLogTableOptions, useMazdaSqlLogs } from '@/composables/mazda/useMazdaSqlLogs';

const props = defineProps({
  serverName: { type: String, required: true },
  mazdaPost: { type: Function, required: true },
});

const columnLabels = {
  rowNo: '序号',
  vin: 'VIN',
  filename: '文件名',
  boxinfo: '设备信息',
  carinfo: '车型信息',
  diagid: '诊断 ID',
  diagver: '诊断版本',
  time: '时间',
  language: '语言',
  ip: 'IP',
  locmac: '本地 MAC',
  wifimac: 'WiFi MAC',
  wifiname: 'WiFi 名称',
  type: '类型',
  versionLength: '版本长度',
  version: '版本内容',
};

const getSqlLogColumnLabel = (column) => columnLabels[column] || column;

const getSqlLogColumnMinWidth = (column) => {
  if (column === 'rowNo') return 80;
  if (column === 'version') return 420;
  if (column === 'filename' || column === 'boxinfo' || column === 'carinfo') return 240;
  if (column === 'vin') return 210;
  if (column === 'time') return 170;
  return 140;
};

const {
  sqlLogTable,
  sqlLogItems,
  sqlLogColumns,
  sqlLogTotal,
  sqlLogHasMore,
  sqlLogExactTotal,
  sqlLogPage,
  sqlLogPageSize,
  sqlLogKeyword,
  sqlLogJumpPage,
  sqlLogPageStatusText,
  sqlLogTotalText,
  sqlLogLoading,
  sqlLogDownloading,
  sqlLogDetailVisible,
  sqlLogDetailText,
  sqlLogTableDesc,
  loadSqlLogs,
  downloadSqlLogs,
  handleSqlLogTableChange,
  handleSqlLogSearch,
  handleSqlLogPageChange,
  handleSqlLogSizeChange,
  handleSqlLogJump,
  showSqlLogDetail,
} = useMazdaSqlLogs(props.mazdaPost, toRef(props, 'serverName'));
</script>

<style scoped>
.sql-log-panel {
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.workspace-card {
  border-radius: 24px;
  border: 1px solid rgba(225, 232, 242, 0.95);
  box-shadow: 0 12px 30px rgba(29, 46, 76, 0.07);
}

.workspace-card :deep(.el-card__body) {
  padding-top: 16px;
  padding-bottom: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  font-weight: 800;
  color: #1f2d3d;
}

.header-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.sql-toolbar,
.sql-pagination-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.sql-toolbar :deep(.el-button),
.dialog-footer :deep(.el-button) {
  min-width: 96px;
}

.sql-search-input {
  width: 360px;
}

.sql-summary-strip {
  margin-top: 16px;
  display: grid;
  grid-template-columns: minmax(220px, 0.7fr) minmax(420px, 1.5fr);
  gap: 12px;
}

.sql-summary-item {
  padding: 14px 16px;
  border-radius: 16px;
  background: linear-gradient(180deg, #fbfcff, #f6f9ff);
  border: 1px solid #ecf2ff;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.sql-summary-item span {
  color: #8a94a6;
  font-size: 12px;
}

.sql-summary-item strong {
  color: #2f3441;
  word-break: break-all;
}

.sql-total-text,
.sql-pagination-left,
.pager-label {
  color: #606266;
  font-weight: 600;
}

.table-scroll-shell {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  padding-bottom: 4px;
}

.table-scroll-inner {
  min-width: 100%;
}

.sql-table-scroll-inner {
  min-width: 1480px;
}

.result-table {
  width: 100%;
}

.result-table :deep(.el-table__inner-wrapper::before) {
  height: 0;
}

.pagination-wrap,
.sql-pagination-wrap {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
  margin-top: 18px;
}

.page-size-select {
  width: 110px;
}

.quick-pager {
  display: inline-flex;
  align-items: center;
  gap: 10px;
}

.jump-input {
  width: 72px;
}

.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 12px;
}

.sql-detail-textarea :deep(.el-textarea__inner) {
  font-family: Consolas, "Courier New", monospace;
  line-height: 1.65;
  border-radius: 18px;
}

:deep(.action-column .cell) {
  overflow: visible;
  text-overflow: clip;
  white-space: nowrap;
  padding-left: 8px;
  padding-right: 8px;
}

@media (max-width: 1180px) {
  .sql-summary-strip {
    grid-template-columns: 1fr;
  }

  .sql-search-input {
    width: 100%;
  }

  .sql-toolbar,
  .sql-pagination-right {
    align-items: stretch;
  }
}
</style>
